import {
  AspAgingBucketKey,
  AspAgingSummary,
  BodyText,
  Tooltip,
  UtilButton,
  formattedCurrency,
  withStyles,
} from 'vopay';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AgingOverview.module.scss';
import { AGING_BUCKETS, getBucketPercent, getOverdueAmount, zeroSummary } from './agingSummary.helper';
import useAgingBucketLabels from '../useAgingBucketLabels';

import { Skeleton } from '#shared/components/UI';

const cn = withStyles(styles);

type AgingOverviewProps = {
  // One summary per currency with open items, and the (deduplicated) list
  // of those currencies the toggle offers.
  summaries: AspAgingSummary[];
  currencies: string[];
  type: 'Payable' | 'Receivable';
  // Currency the zero state renders in when there are no open items.
  emptyStateCurrency: string;
  isFetching: boolean;
  // The card shows one currency at a time — no FX conversion (Figma
  // "Payables / Currency Toggle").
  currency: string;
  onCurrencyChange: (currency: string) => void;
  selectedBucket: AspAgingBucketKey | null;
  // Fires for the selected bucket too — the container toggles it off.
  onBucketClick: (bucket: AspAgingBucketKey, currency: string) => void;
};

export default function AgingOverview({
  summaries,
  currencies,
  type,
  emptyStateCurrency,
  isFetching,
  currency,
  onCurrencyChange,
  selectedBucket,
  onBucketClick,
}: AgingOverviewProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.AgingOverview' });
  const bucketLabels = useAgingBucketLabels();

  // Collapsed on every load; the choice is not remembered (ticket).
  const [isAgingShown, setIsAgingShown] = useState(false);

  if (isFetching && !summaries.length) {
    // Same footprint as the loaded collapsed card so nothing shifts when the
    // summary lands.
    return (
      <div className={cn('card')} data-testid="aging-overview">
        <Skeleton height={122} borderRadius={16} />
      </div>
    );
  }

  const summary =
    summaries.find((candidate) => (candidate.Currency || emptyStateCurrency) === currency) ?? zeroSummary();

  const total = summary.TotalAmount;
  const overdue = getOverdueAmount(summary);

  const items =
    type === 'Receivable'
      ? t('itemsReceivable', { defaultValue: 'invoices' })
      : t('itemsPayable', { defaultValue: 'bills' });

  const totalOwedLabel =
    type === 'Receivable'
      ? t('totalOwedReceivable', { defaultValue: 'Total owed by customers' })
      : t('totalOwedPayable', { defaultValue: 'Total owed to suppliers' });

  const bucketTooltips: { [bucket in AspAgingBucketKey]: string } = {
    Current: t('currentTooltip', { defaultValue: 'See {{currency}} {{items}} not yet due', currency, items }),
    PastDue1To30: t('pastDueTooltip', {
      defaultValue: 'See {{currency}} {{items}} past due {{range}} days',
      currency,
      items,
      range: '1–30',
    }),
    PastDue31To60: t('pastDueTooltip', {
      defaultValue: 'See {{currency}} {{items}} past due {{range}} days',
      currency,
      items,
      range: '31–60',
    }),
    PastDue61To90: t('pastDueTooltip', {
      defaultValue: 'See {{currency}} {{items}} past due {{range}} days',
      currency,
      items,
      range: '61–90',
    }),
    PastDue91Plus: t('pastDue91PlusTooltip', {
      defaultValue: 'See {{currency}} {{items}} past due 91+ days',
      currency,
      items,
    }),
  };

  const renderAmount = (amount: number, amountClassName: string) => (
    <div className={cn('amountRow')}>
      <BodyText size="sm" weight="em" className={cn('code')}>
        {currency}
      </BodyText>

      <span className={cn(amountClassName)}>{formattedCurrency(amount.toFixed(2))}</span>
    </div>
  );

  return (
    <div className={cn('card')} data-testid="aging-overview">
      <div className={cn('header')}>
        <div className={cn('totals')}>
          <div className={cn('total')} data-testid="aging-total">
            <div className={cn('label')}>{totalOwedLabel}</div>
            {renderAmount(total, 'headlineAmount')}
          </div>

          <div className={cn('total', 'overdue')} data-testid="aging-overdue">
            <div className={cn('label')}>{t('overdue', { defaultValue: 'Overdue' })}</div>
            {renderAmount(overdue, 'headlineAmount')}
          </div>
        </div>

        {currencies.length > 1 && (
          <div
            role="group"
            aria-label={t('currencyToggleLabel', { defaultValue: 'Summary currency' })}
            className={cn('currencyToggle')}
          >
            {currencies.map((option) => (
              <button
                key={option}
                type="button"
                className={cn('currencyOption', option === currency && 'active')}
                aria-pressed={option === currency}
                onClick={() => {
                  if (option !== currency) onCurrencyChange(option);
                }}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>

      {isAgingShown && (
        <div className={cn('aging')}>
          {total > 0 && (
            <div className={cn('barWrap')}>
              <div className={cn('bar')}>
                {AGING_BUCKETS.map((bucket) => (
                  <div
                    key={bucket}
                    className={cn('segment', `fill-${bucket}`, selectedBucket && selectedBucket !== bucket && 'faded')}
                    data-testid={`aging-segment-${bucket}`}
                    // Each segment's width is its share of the total — data,
                    // not layout, so it can't live in the stylesheet.
                    style={{ flexGrow: summary.AgingBuckets[bucket].Amount }}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={cn('tiles')}>
            {AGING_BUCKETS.map((bucket) => {
              const amount = summary.AgingBuckets[bucket].Amount;
              const isSelected = selectedBucket === bucket;

              return (
                <div key={bucket} className={cn('tileWrap')}>
                  <Tooltip
                    type="hover"
                    position="top"
                    hasButton={false}
                    information={bucketTooltips[bucket]}
                    customElement={
                      <button
                        type="button"
                        className={cn('tile', isSelected && 'selected')}
                        data-testid={`aging-tile-${bucket}`}
                        aria-pressed={isSelected}
                        aria-label={`${bucketLabels[bucket]} — ${bucketTooltips[bucket]}`}
                        onClick={() => onBucketClick(bucket, currency)}
                      >
                        <div className={cn('labelRow')}>
                          <span className={cn('swatch', `fill-${bucket}`)} />
                          <span className={cn('label')}>{bucketLabels[bucket]}</span>
                        </div>

                        {renderAmount(amount, 'tileAmount')}

                        <div className={cn('label')}>
                          {t('percentOfTotal', {
                            defaultValue: '{{percent}}% of total',
                            percent: getBucketPercent(amount, total),
                          })}
                        </div>
                      </button>
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={cn('footer')}>
        <UtilButton
          size="small"
          iconRight={isAgingShown ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          aria-expanded={isAgingShown}
          onClick={() => setIsAgingShown((shown) => !shown)}
        >
          {isAgingShown
            ? t('hideAging', { defaultValue: 'Hide aging' })
            : t('showAging', { defaultValue: 'Show aging' })}
        </UtilButton>
      </div>
    </div>
  );
}
